import type { ErrorInfo } from "react";
import React, {
  Component,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { AbilityContext } from "../Can";
import { defineAbilitiesFor } from "../utils/abilities";

import { Button, Center, Loader, Stack, Text } from "@mantine/core";
import { useAuthStore } from "@utils/stores/authStore.ts";

interface CASLProviderProps {
  children: React.ReactNode;
  showLoadingScreen?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

// Error boundary component for CASL provider
class CASLErrorBoundary extends Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("CASL Provider Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Center w="100vw" h="100vh">
          <Stack align="center" gap="md">
            <Text size="lg" fw="bold" c="red">
              Authorization Error
            </Text>
            <Text ta="center" c="dimmed">
              There was a problem loading user permissions.
            </Text>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Stack>
        </Center>
      );
    }

    return <>{this.props.children}</>;
  }
}

// Helper function to map role formats
const mapRoleFormat = (authRole: string | null): string | null => {
  if (!authRole) return null;

  switch (authRole) {
    case "team lead":
      return "team_lead";
    case "team member":
      return "team_member";
    default:
      return authRole;
  }
};

// Create context for role management
interface RoleContextType {
  role: string | null;
  isBusinessRole: boolean;
  isClient: boolean;
  loading: boolean;
}

const RoleContext = createContext<RoleContextType | null>(null);

// Enhanced CASLProvider with role context
const CASLProviderWithRole: React.FC<CASLProviderProps> = ({
  children,
  showLoadingScreen = true,
}) => {
  const { user, loading } = useAuthStore();
  const [ability, setAbility] = useState(() => {
    try {
      return defineAbilitiesFor(null);
    } catch (error) {
      console.error("Failed to initialize default abilities:", error);
      return defineAbilitiesFor(null);
    }
  });

  const role = mapRoleFormat(user?.role || null);
  const isBusinessRole = role !== null && role !== "client";
  const isClient = role === "client";

  useEffect(() => {
    if (!loading) {
      try {
        setAbility(defineAbilitiesFor(role));
      } catch (error) {
        console.error("Failed to set user abilities:", error);
      }
    }
  }, [user?.role, loading, role]);

  if (loading && showLoadingScreen) {
    return (
      <Center w="100vw" h="100vh">
        <Loader color="blue" />
      </Center>
    );
  }

  const roleContextValue: RoleContextType = {
    role,
    isBusinessRole,
    isClient,
    loading,
  };

  return (
    <CASLErrorBoundary>
      <RoleContext.Provider value={roleContextValue}>
        <AbilityContext.Provider value={ability}>
          {children}
        </AbilityContext.Provider>
      </RoleContext.Provider>
    </CASLErrorBoundary>
  );
};

// Hook to use role context
export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a CASLProvider");
  }
  return context;
};

// Higher-order component for pages that need role-based loading
export const withRoleBasedLoading = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { loading } = useAuthStore();

    if (loading) {
      return (
        <Center w="100vw" h="100vh">
          <Loader color="blue" />
        </Center>
      );
    }

    return <Component {...props} />;
  };
};

export default CASLProviderWithRole;
